import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { toRegex } from 'components/scommesseFilterTertiary';
import { ScommesseTableTitle } from 'components/scommesseTable/scommesseTableTitle';
import { ScommessaKind } from 'components/scommesseTipo/types';
import { TooltipPosition } from 'components/tooltip';
import { AppFragment } from 'enums/path-fragment';
import {
  useAvvenimento,
  useInfoTipo,
  useIsInfoAggActiveByScommessaKey,
  useScommessa,
  useScommessaStatus,
} from 'features/sport/hooks';
import { KeyManagerSport } from 'features/sport/utils/keyManager';
import { useTranslation } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { VisualizationEnum } from 'lib/api/sport/sportScommesseBySlugResponse';
import { useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import { calculateHeaderWidth, isCrawler, isTruthy } from 'utility/functions';
import { SportScommesseRenderConditionalProps } from 'features/sport/components/sportScommesseRenderConditional';
import { ConditionalContainer } from 'features/sport/components/sportScommesseRenderConditional/ConditionalContainer';
import styles from './SportScommesseRenderConditional.module.scss';
import { SportScommesseRenderSkeleton } from 'features/sport/components/sportScommesseRenderConditional/SportScommesseRenderSkeleton';

const indexing = isCrawler();

export const SportScommesseRenderConditional = memo(function SportScommesseRenderConditional(
  props: SportScommesseRenderConditionalProps
) {
  const {
    index,
    hasTitle,
    isEstesa,
    hasHeader,
    forceShow,
    isAntepost,
    scommessaKey,
    bypassFilter = false,
    isTooltipClicked,
    containerClassName,
    showOnlyBilanciata,
    isManifestazioneTemplate,
    gtagAction,
    setIsTooltipClicked,
  } = props ?? {};

  const { t } = useTranslation();

  const { pathname } = useRouter();
  const isDetail = useMemo(
    () =>
      [
        `/${AppFragment.Live}/[disciplina]/[...dettaglio-avvenimento]`,
        `/${AppFragment.Sport}/[disciplina]/[manifestazione]/[avvenimento]`,
      ].includes(pathname),
    [pathname]
  );

  const { isMobile } = useWindowSize();

  const km = useMemo(() => new KeyManagerSport(scommessaKey), [scommessaKey]);

  const scommessa = useScommessa(scommessaKey);
  const avvenimento = useAvvenimento(km.avvenimentoKey);
  const isInfoAggActive = useIsInfoAggActiveByScommessaKey(scommessaKey);
  const infoTipoScommessa = useInfoTipo(km.tipoScommessaId);
  const avvenimentoFilter = useTypedSelector((state) => state.sport.avvenimentoFilter);

  const { isLoading } = useScommessaStatus();

  if (indexing || isLoading) {
    return <SportScommesseRenderSkeleton {...props} />;
  }

  // do not render when scommessa does not fit selected filters
  if (isTruthy(avvenimentoFilter?.length) && !bypassFilter) {
    const regex = toRegex(avvenimentoFilter);
    // const {descrizioneTrKey, descrizione: fallback} = infoTipoScommessa ?? {}
    // const descrizione = t(descrizioneTrKey, fallback);

    if (!isTruthy(`${infoTipoScommessa?.descrizione}`.match(regex)?.length)) {
      return null;
    }
  }

  // Nascondi se disattiva
  if (
    isDetail &&
    !forceShow &&
    !isInfoAggActive
    // Nascondere solo nei dettagli
    // Probabilmente vale la pena fare un check centralizzato
  ) {
    return null;
  }

  try {
    if (!scommessa || !avvenimento) throw infoTipoScommessa;

    const headerWidth = calculateHeaderWidth(
      infoTipoScommessa?.tipoVisualizzazione!,
      infoTipoScommessa?.sizeInfoAggiuntive!,
      infoTipoScommessa?.numeroEsiti!,
      infoTipoScommessa?.headers!
    );

    const componentList = {
      // Scommessa fino a 3 Esiti
      [VisualizationEnum.EsitiUnderThree]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
            })}
          >
            {hasTitle ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                headerWidth={headerWidth}
                isEstesa={isEstesa}
                isTooltipClicked={isTooltipClicked}
                setIsTooltipClicked={setIsTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              scommessaTipo={ScommessaKind.ScommesseTipoEsitiUnderThree}
              scommessaKey={scommessaKey}
              header={t(infoTipoScommessa?.descrizioneTrKey, infoTipoScommessa?.descrizione)}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Scommessa con esiti maggiori di 3
      [VisualizationEnum.EsitiOverThree]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
              [styles.order]: isEstesa,
              [styles.containerWidth]: isEstesa,
            })}
          >
            {hasTitle || (isEstesa && !isMobile) ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                headerWidth={headerWidth}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              scommessaTipo={
                isEstesa ? ScommessaKind.ScommesseTipoEsitiOverThreeEstesa : ScommessaKind.ScommesseTipoEsitiOverThree
              }
              scommessaKey={scommessaKey}
              hasHeader={hasHeader}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Scommessa Antepost
      [VisualizationEnum.Antepost]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
            })}
          >
            {hasTitle ? (
              <ScommesseTableTitle
                labelDx={`${infoTipoScommessa?.descrizione} ${avvenimento?.descrizione}`}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              scommessaTipo={ScommessaKind.ScommesseTipoAntepost}
              scommessaKey={scommessaKey}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Scommessa Info Aggiuntiva Unica con esiti fino a 3
      [VisualizationEnum.SingleInfoAggEsitiUnderThree]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
              [styles.order]: isEstesa,
              [styles.containerWidth]: isEstesa,
            })}
          >
            {hasTitle || (isEstesa && !isMobile) ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                headerWidth={headerWidth}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              scommessaTipo={
                isEstesa
                  ? ScommessaKind.ScommesseTipoEsitiUnderThreeWithInfoAggEstesa
                  : showOnlyBilanciata
                  ? ScommessaKind.ScommesseTipoEsitiUnderThreeWithBilanciata
                  : ScommessaKind.ScommesseTipoEsitiUnderThreeWithInfoAgg
              }
              scommessaKey={scommessaKey}
              hasHeader={!isEstesa && hasHeader}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Scommessa Info Aggiuntiva Unica con esiti maggiori di 3
      [VisualizationEnum.SingleInfoAggEsitiOverThree]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
              [styles.order]: isEstesa,
              [styles.containerWidth]: isEstesa,
            })}
          >
            {hasTitle || (isEstesa && !isMobile) ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(isEstesa ? styles.estesaTitle : styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                headerWidth={headerWidth}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              scommessaTipo={
                isEstesa
                  ? ScommessaKind.ScommesseTipoEsitiOverThreeWithInfoAggEstesa
                  : ScommessaKind.ScommesseTipoEsitiOverThreeWithInfoAgg
              }
              scommessaKey={scommessaKey}
              hasHeader={!isEstesa && hasHeader}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Scommessa Info Aggiuntive Multiple con esiti fino a 3
      [VisualizationEnum.MultipleInfoAggEsitiUnderThree]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
            })}
          >
            {hasTitle ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                headerWidth={headerWidth}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={isMobile || isEstesa ? TooltipPosition.BOTTOM_LEFT : TooltipPosition.BOTTOM}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              showOnlyBilanciata={showOnlyBilanciata}
              scommessaTipo={ScommessaKind.ScommesseTipoWithInfoAggMultiple}
              scommessaKey={scommessaKey}
              hasHeader={hasHeader}
              isEstesa={isEstesa}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
      // Info Aggiuntiva Multiple dipendenti tra di loro + Esiti maggiori di 3:
      [VisualizationEnum.RecursiveInfoAgg]: () => {
        return (
          <ConditionalContainer
            index={index}
            scommessaKey={scommessaKey}
            className={classNames(containerClassName, styles[`type${infoTipoScommessa?.tipoVisualizzazione}`], {
              [styles.full]: isEstesa,
              [styles.order]: isEstesa, // TODO VERIFY
              [styles.containerWidth]: isEstesa, // TODO VERIFY
            })}
          >
            {hasTitle || (isEstesa && !isMobile) ? (
              <ScommesseTableTitle
                labelDx={infoTipoScommessa?.descrizione}
                labelDxTrKey={infoTipoScommessa?.descrizioneTrKey}
                className={classNames(styles.mainTitle, {
                  [styles.removeTitle]: isManifestazioneTemplate,
                  [styles.titleAntepost]: isAntepost,
                })}
                tooltipText={infoTipoScommessa?.toolTip}
                tooltipTextTrKey={infoTipoScommessa?.toolTipTrKey}
                tooltipPosition={TooltipPosition.BOTTOM_LEFT}
                isEstesa={isEstesa}
                setIsTooltipClicked={setIsTooltipClicked}
                isTooltipClicked={isTooltipClicked}
                infoTipoScommessaKey={infoTipoScommessa.key}
              />
            ) : null}
            <ConditionalContainer.Content
              className={classNames(styles.containerWrap, { [styles.estesa]: isEstesa })}
              showOnlyBilanciata={showOnlyBilanciata}
              scommessaTipo={ScommessaKind.ScommesseTipoWithInfoAggMultiple}
              scommessaKey={scommessaKey}
              hasHeader={hasHeader}
              isEstesa={isEstesa}
              gtagAction={gtagAction}
            />
          </ConditionalContainer>
        );
      },
    };

    return infoTipoScommessa ? componentList[infoTipoScommessa.tipoVisualizzazione]() : null;
  } catch (error) {
    return <SportScommesseRenderSkeleton {...props} />;
  }
});